import _map from 'lodash/map';
import { type FC, useCallback, } from 'react';
import { useSelector, } from 'react-redux';
import classNames from 'classnames';

import { T, TM } from '@web-solutions/module-localization';
import Analytics from '@web-solutions/module-analytics';

import { EVENT_ACTION } from '@web-solutions/core/constants/general';

import { type ProductDetails, selectProducts } from '@web-solutions/core/store/billing/selectors';

import { Title } from '@web-solutions/core/ui-elements';

import { useRemoteConfig } from '@web-solutions/core/hooks/use-remote-config';

import { ProductsTexts } from 'core/summary';

import { SpecialOfferBase } from '../special-offer-base'

import { tKey } from '../constants';

import { SpecialOfferDiscountBoard, SpecialOfferTimer } from '../sections';
import { Item } from '../../plans/item';

import type { SpectialOfferTypeProps } from '../types'

import styles from './style.module.scss';

interface SpecialOfferWithPlansProps extends SpectialOfferTypeProps {
  onProductClick: (product: ProductDetails) => void;
  productsTexts: ProductsTexts[];
}

export const SpecialOfferWithPlans: FC<SpecialOfferWithPlansProps> = ({
  isOpen,
  activeProduct,
  onProductClick,
  onGetClick,
  onClose,
  productsTexts,
}) => {
  const products = useSelector(selectProducts);

  const { specialOffer } = useRemoteConfig();

  const isSecondVersion = specialOffer.mode === 'with_plans_v2'

  const handleProductClick = useCallback((product: ProductDetails) => {
    Analytics.trackEvent('special_offer_product', EVENT_ACTION.CLICK, { productId: product.id });
    onProductClick(product);
  }, [onProductClick]);

  return (
    <SpecialOfferBase
      mode={specialOffer.mode}
      className={isSecondVersion ? styles.modal : ''}
      withCloseButton={!isSecondVersion}
      withTerms={isSecondVersion}
      isOpen={isOpen}
      activeProduct={activeProduct}
      onClose={onClose}
      onGetClick={onGetClick}
    >
      {isSecondVersion &&
        <>
          <SpecialOfferTimer className={styles.timer} />
          <SpecialOfferDiscountBoard
            className={styles.discount_board}
            discount={specialOffer.discount}
            prevDiscount={specialOffer.prevDiscount}
          />
        </>
      }
      <Title level='h2' className={classNames(styles.title, { [styles.compact]: isSecondVersion })}>
        <TM
          k={specialOffer.title}
          defaultKey={`${tKey}${isSecondVersion ? `.${specialOffer.mode}` : ''}.title`}
          tOptions={{ discount: specialOffer.discount }}
        />
      </Title>
      {
        !isSecondVersion &&
        <Title className={styles.discount}>
          <T k={`${tKey}.discount`} tOptions={{ discount: specialOffer.discount }} />
        </Title>
      }
      {
        _map(products, (product) => {
          const currentProductTexts = productsTexts.find((text) => text.id === product.id)!;
          return(
          <Item
            key={product.id}
            isActive={activeProduct?.id === product.id}
            onClick={handleProductClick}
            product={product}
            titleText={currentProductTexts.titleText}
            subTitleText={currentProductTexts.subTitleText}
            perText={currentProductTexts.perText}
            perTitle={currentProductTexts.perTitle}
          />
          )})
      }
    </SpecialOfferBase >
  );
};
